/**
 * ExportCenter - Stories 24.69-24.71
 * Content export with multiple format options
 */

import React, { useState } from 'react';
import {
  Download, FileText, FileSpreadsheet, FileImage, File,
  Copy, Check, Mail, Share2, Link, ChevronDown,
  Loader2, CheckCircle, AlertCircle
} from 'lucide-react';

export type ExportFormat = 'pdf' | 'docx' | 'html' | 'markdown' | 'json' | 'csv' | 'png' | 'zip';
export type ExportStatus = 'idle' | 'preparing' | 'ready' | 'error';

export interface ExportOption {
  format: ExportFormat;
  label: string;
  description: string;
  icon: React.ElementType;
  available: boolean;
}

interface ExportCenterProps {
  title: string;
  description?: string;
  formats: ExportOption[];
  onExport: (format: ExportFormat, options?: ExportOptions) => Promise<string | Blob>;
  onShare?: (method: 'email' | 'link') => Promise<string>;
  onCopy?: () => Promise<void>;
  showShareOptions?: boolean;
  className?: string;
}

interface ExportOptions {
  includeMetadata?: boolean;
  includeComments?: boolean;
  includeVersionHistory?: boolean;
  quality?: 'draft' | 'standard' | 'high';
}

const formatIcons: Record<ExportFormat, React.ElementType> = {
  pdf: FileText,
  docx: FileText,
  html: File,
  markdown: FileText,
  json: File,
  csv: FileSpreadsheet,
  png: FileImage,
  zip: File
};

export const ExportCenter: React.FC<ExportCenterProps> = ({
  title,
  description,
  formats,
  onExport,
  onShare,
  onCopy,
  showShareOptions = true,
  className = ''
}) => {
  const [selectedFormat, setSelectedFormat] = useState<ExportFormat | null>(null);
  const [exportStatus, setExportStatus] = useState<ExportStatus>('idle');
  const [exportOptions, setExportOptions] = useState<ExportOptions>({
    includeMetadata: true,
    includeComments: false,
    includeVersionHistory: false,
    quality: 'standard'
  });
  const [copied, setCopied] = useState(false);
  const [shareLink, setShareLink] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleExport = async (format: ExportFormat) => {
    setSelectedFormat(format);
    setExportStatus('preparing');
    setError(null);

    try {
      const result = await onExport(format, exportOptions);
      setExportStatus('ready');
      
      // Trigger download if result is a Blob
      if (result instanceof Blob) {
        const url = URL.createObjectURL(result);
        const a = document.createElement('a');
        a.href = url;
        a.download = `export.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      } else if (typeof result === 'string') {
        // If string URL, redirect
        window.open(result, '_blank');
      }

      // Reset after delay
      setTimeout(() => {
        setExportStatus('idle');
        setSelectedFormat(null);
      }, 3000);
    } catch (err) {
      setExportStatus('error');
      setError(err instanceof Error ? err.message : 'Export failed');
    }
  };

  const handleCopy = async () => {
    if (!onCopy) return;
    try {
      await onCopy();
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      // Handle error
    }
  };

  const handleShare = async (method: 'email' | 'link') => {
    if (!onShare) return;
    try {
      const link = await onShare(method);
      if (method === 'link') {
        setShareLink(link);
        navigator.clipboard.writeText(link);
      }
    } catch (err) {
      // Handle error
    }
  };

  return (
    <div className={`bg-white rounded-xl border border-gray-200 ${className}`}>
      {/* Header */}
      <div className="p-4 border-b border-gray-200">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
            <Download className="w-5 h-5 text-blue-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">{title}</h3>
            {description && <p className="text-sm text-gray-500">{description}</p>}
          </div>
        </div>
      </div>

      {/* Export formats */}
      <div className="p-4">
        <h4 className="text-sm font-medium text-gray-700 mb-3">Export Format</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
          {formats.map((format) => {
            const Icon = format.icon;
            const isSelected = selectedFormat === format.format;
            const isExporting = isSelected && exportStatus === 'preparing';
            const isReady = isSelected && exportStatus === 'ready';
            const hasError = isSelected && exportStatus === 'error';

            return (
              <button
                key={format.format}
                onClick={() => handleExport(format.format)}
                disabled={!format.available || isExporting}
                className={`p-3 rounded-lg border-2 text-left transition-all ${
                  !format.available
                    ? 'border-gray-100 bg-gray-50 opacity-50 cursor-not-allowed'
                    : isSelected
                    ? 'border-blue-500 bg-blue-50'
                    : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }`}
              >
                <div className="flex items-center gap-2">
                  {isExporting ? (
                    <Loader2 className="w-5 h-5 text-blue-500 animate-spin" />
                  ) : isReady ? (
                    <CheckCircle className="w-5 h-5 text-green-500" />
                  ) : hasError ? (
                    <AlertCircle className="w-5 h-5 text-red-500" />
                  ) : (
                    <Icon className="w-5 h-5 text-gray-600" />
                  )}
                  <div>
                    <div className="font-medium text-gray-800 text-sm">{format.label}</div>
                    <div className="text-xs text-gray-500">{format.description}</div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </div>

      {/* Export options */}
      <div className="px-4 pb-4">
        <details className="group">
          <summary className="flex items-center gap-2 text-sm text-gray-600 cursor-pointer list-none">
            <ChevronDown className="w-4 h-4 group-open:rotate-180 transition-transform" />
            Export Options
          </summary>
          <div className="mt-3 p-3 bg-gray-50 rounded-lg space-y-2">
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={exportOptions.includeMetadata}
                onChange={(e) => setExportOptions(prev => ({ ...prev, includeMetadata: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-700">Include metadata</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={exportOptions.includeComments}
                onChange={(e) => setExportOptions(prev => ({ ...prev, includeComments: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-700">Include comments</span>
            </label>
            <label className="flex items-center gap-2">
              <input
                type="checkbox"
                checked={exportOptions.includeVersionHistory}
                onChange={(e) => setExportOptions(prev => ({ ...prev, includeVersionHistory: e.target.checked }))}
                className="w-4 h-4 text-blue-600 rounded"
              />
              <span className="text-sm text-gray-700">Include version history</span>
            </label>
            <div className="flex items-center gap-2 pt-2">
              <span className="text-sm text-gray-700">Quality:</span>
              {(['draft', 'standard', 'high'] as const).map((q) => (
                <button
                  key={q}
                  onClick={() => setExportOptions(prev => ({ ...prev, quality: q }))}
                  className={`px-2 py-1 text-xs rounded capitalize transition-colors ${
                    exportOptions.quality === q
                      ? 'bg-blue-100 text-blue-700'
                      : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                  }`}
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        </details>
      </div>

      {/* Error message */}
      {error && (
        <div className="mx-4 mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">
          <AlertCircle className="w-4 h-4 inline mr-2" />
          {error}
        </div>
      )}

      {/* Share options */}
      {showShareOptions && (onCopy || onShare) && (
        <div className="p-4 border-t border-gray-200 bg-gray-50">
          <h4 className="text-sm font-medium text-gray-700 mb-3">Quick Actions</h4>
          <div className="flex gap-2">
            {onCopy && (
              <button
                onClick={handleCopy}
                className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-white transition-colors"
              >
                {copied ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                {copied ? 'Copied!' : 'Copy content'}
              </button>
            )}
            {onShare && (
              <>
                <button
                  onClick={() => handleShare('email')}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-white transition-colors"
                >
                  <Mail className="w-4 h-4" />
                  Email
                </button>
                <button
                  onClick={() => handleShare('link')}
                  className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-white transition-colors"
                >
                  <Link className="w-4 h-4" />
                  Get link
                </button>
              </>
            )}
          </div>
          {shareLink && (
            <div className="mt-3 p-2 bg-white rounded border border-gray-200 flex items-center gap-2">
              <input
                type="text"
                value={shareLink}
                readOnly
                className="flex-1 text-sm text-gray-600 bg-transparent outline-none"
              />
              <Check className="w-4 h-4 text-green-500" />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ExportCenter;
